import { isFullContentWidth, isMaxContentWidth, isUnifiedGap, isAutoPosition, isAutoRowOrColumnSize, isMaxContentRowOrColumnSize, isMinContentRowOrColumnSize, isSizedContentRowOrColumnSize, isGridContainerLayout, isStackContainerLayout, isMinMaxRowOrColumnSize, isPegboardContainerLayout } from 'layout-data-lib/GridLayoutDataTree/typenameUtils';
import { AUTO_FLOW_TYPES } from 'layout-data-lib/GridLayoutDataTree/constants';
import { getContainerBreakpointLayout, isSectionNode } from 'layout-data-lib/GridLayoutDataTree/utils';
import { DEFAULT_COLUMN_SIZE_DEFINITION, DEFAULT_ROW_SIZE_DEFINITION } from 'layout-data-lib/GridLayoutDataTree/layoutBuilderUtils';
import { getPegboardContainerWidth, getPegboardRowSize } from 'layout-dnd-utils/geometry/pegboard';
const DIRECTIONS = ['top', 'bottom', 'left', 'right'];
const UNITS_CSS_LOOKUP = {
  CM: 'cm',
  MM: 'mm',
  Q: 'Q',
  IN: 'in',
  PC: 'pc',
  PT: 'pt',
  PX: 'px',
  EM: 'em',
  EX: 'ex',
  CH: 'ch',
  REM: 'rem',
  LH: 'lh',
  VW: 'vw',
  VH: 'vh',
  VMIN: 'vmin',
  VMAX: 'vmax',
  PERCENTAGE: '%',
  FRACTION: 'fr',
  AUTO: 'auto',
  MIN_CONTENT: 'min-content',
  MAX_CONTENT: 'max-content',
  FIT_CONTENT: 'fit-content'
};
const CSS_TO_CHIRP_UNIT_LOOKUP = {
  cm: 'CM',
  mm: 'MM',
  q: 'Q',
  in: 'IN',
  pc: 'PC',
  pt: 'PT',
  px: 'PX',
  em: 'EM',
  ex: 'EX',
  ch: 'CH',
  rem: 'REM',
  lh: 'LH',
  vw: 'VW',
  vh: 'VH',
  vmin: 'VMIN',
  vmax: 'VMAX',
  '%': 'PERCENTAGE',
  fr: 'FRACTION'
};
export function lookupCssValue(value) {
  // @ts-expect-error technically the CHIRP type as an opaque type that can't be used as an index
  return UNITS_CSS_LOOKUP[value];
}
export function writeMargin(margin) {
  return DIRECTIONS.reduce((acc, direction) => {
    const value = margin[direction];
    if (value) {
      acc.push(`margin-${direction}: ${value.value}${lookupCssValue(value.units)};`);
    }
    return acc;
  }, []).join('\n');
}
export function writePadding(padding) {
  return DIRECTIONS.reduce((acc, direction) => {
    const value = padding[direction];
    if (value) {
      acc.push(`padding-${direction}: ${value.value}${lookupCssValue(value.units)};`);
    }
    return acc;
  }, []).join('\n');
}
export function writeUnitValue(value) {
  if (value) {
    return `${value.value}${lookupCssValue(value.units)}`;
  }
  return '';
}
export function writeGap(value) {
  if (isUnifiedGap(value)) {
    return `gap: ${writeUnitValue(value.size)};`;
  }
  return `row-gap: ${writeUnitValue(value.rowGap)}; \n column-gap: ${writeUnitValue(value.columnGap)};`;
}
export function writeMaxWidthToCss(maxWidth, stylesForBreakpoint) {
  if (isMaxContentWidth(maxWidth)) {
    stylesForBreakpoint.push(`max-width: ${maxWidth.size.value}${lookupCssValue(maxWidth.size.units)};`);
  }
  if (isFullContentWidth(maxWidth)) {
    stylesForBreakpoint.push(`max-width: 100%;`);
  }
}
export function writePositionCss(position, rowOrColumn) {
  if (isAutoPosition(position)) {
    return 'auto';
  }
  return rowOrColumn === 'row' ? position.rowStart.toString() : position.columnStart.toString();
}
function writeSpanning(spanning, rowOrColumn) {
  const spanTarget = rowOrColumn === 'row' ? spanning.rowsSpanned : spanning.columnsSpanned;
  if (!spanTarget) {
    return undefined;
  }
  return `span ${spanTarget.toString()}`;
}
export function writeGridColumn({
  position,
  spanning
}) {
  const renderedPosition = position ? writePositionCss(position, 'column') : 'auto';
  const renderedSpan = spanning ? writeSpanning(spanning, 'column') : undefined;
  if (!renderedSpan) {
    return renderedPosition;
  }
  return `${renderedPosition} / ${renderedSpan}`;
}
export function writeGridRow({
  position,
  spanning
}) {
  const renderedPosition = position ? writePositionCss(position, 'row') : 'auto';
  const renderedSpan = spanning ? writeSpanning(spanning, 'row') : undefined;
  if (!renderedSpan) {
    return renderedPosition;
  }
  return `${renderedPosition} / ${renderedSpan}`;
}
export function writeGridPositionToCss(position, spanning) {
  if (!position && !spanning) {
    return '';
  }
  const gridColumnCssRule = writeGridColumn({
    position,
    spanning
  });
  const gridRowCssRule = writeGridRow({
    position,
    spanning
  });
  const gridColumnCssProperty = gridColumnCssRule === 'auto' ? '' : `grid-column: ${gridColumnCssRule};`;
  const gridRowCssProperty = gridRowCssRule === 'auto' ? '' : `grid-row: ${gridRowCssRule};`;
  return `${gridColumnCssProperty} \n ${gridRowCssProperty}`;
}
export function writeRowOrColumnDefinitionsToCss(defs) {
  return defs.map(def => {
    const size = def.size;
    if (isAutoRowOrColumnSize(size)) {
      return 'auto';
    } else if (isMinMaxRowOrColumnSize(size)) {
      const min = `${typeof size.min.value === 'number' ? size.min.value : ''}${lookupCssValue(size.min.units)}`;
      const max = `${typeof size.max.value === 'number' ? size.max.value : ''}${lookupCssValue(size.max.units)}`;
      return `minmax(${min}, ${max})`;
    } else if (isMaxContentRowOrColumnSize(size)) {
      return 'max-content';
    } else if (isMinContentRowOrColumnSize(size)) {
      return 'min-content';
    } else if (isSizedContentRowOrColumnSize(size)) {
      const units = lookupCssValue(size.size.units);
      if (units === UNITS_CSS_LOOKUP.FRACTION) {
        return `minmax(0, ${size.size.value}fr)`;
      }
      return `${size.size.value}${lookupCssValue(size.size.units)}`;
    } else {
      return 'auto';
    }
  }).join(' ');
}
export function writeAutoFlowToCss(autoFlow) {
  switch (autoFlow) {
    case AUTO_FLOW_TYPES.COLUMN:
      return 'column';
    case AUTO_FLOW_TYPES.ROW:
    default:
      return 'row';
  }
}
export function writeLayoutStylesToCss(layoutStyles, existingStylesByBreakpointName, parentNode, viewportWidthBreakpointMap = {}) {
  Object.entries(layoutStyles).forEach(([breakpointName, layout]) => {
    if (isPegboardContainerLayout(layout) && isSectionNode(parentNode)) {
      const stylesForBreakpoint = existingStylesByBreakpointName[breakpointName] || [];
      stylesForBreakpoint.push(`display: grid;`);
      stylesForBreakpoint.push(`grid-template-columns: repeat(${layout.columns.length}, 1fr);`);
      const pegboardContainerWidth = getPegboardContainerWidth(breakpointName, viewportWidthBreakpointMap, parentNode);
      const rowSize = getPegboardRowSize(breakpointName, pegboardContainerWidth);
      stylesForBreakpoint.push(`grid-template-rows: repeat(${layout.rows.length}, minmax(${rowSize.toFixed(2)}px, auto));`);
      existingStylesByBreakpointName[breakpointName] = stylesForBreakpoint;
    } else if (isGridContainerLayout(layout)) {
      const {
        columns,
        rows,
        autoFlow,
        autoRows,
        autoColumns
      } = layout;
      const stylesForBreakpoint = existingStylesByBreakpointName[breakpointName] || [];
      stylesForBreakpoint.push(`display: grid;`);
      stylesForBreakpoint.push(`grid-template-columns: ${writeRowOrColumnDefinitionsToCss(columns)};`);
      stylesForBreakpoint.push(`grid-template-rows: ${writeRowOrColumnDefinitionsToCss(rows)};`);
      if (autoFlow) {
        stylesForBreakpoint.push(`grid-auto-flow: ${writeAutoFlowToCss(autoFlow)};`);
      }
      if (autoRows) {
        stylesForBreakpoint.push(`grid-auto-rows: ${writeRowOrColumnDefinitionsToCss(autoRows)};`);
      }
      if (autoColumns) {
        stylesForBreakpoint.push(`grid-auto-columns: ${writeRowOrColumnDefinitionsToCss(autoColumns)};`);
      }
      existingStylesByBreakpointName[breakpointName] = stylesForBreakpoint;
    } else if (isStackContainerLayout(layout)) {
      const stylesForBreakpoint = existingStylesByBreakpointName[breakpointName] || [];
      stylesForBreakpoint.push(`display: flex;`);
      stylesForBreakpoint.push(`flex-direction: ${layout.direction === 'HORIZONTAL' ? 'row' : 'column'};`);
    }
  });
}
export function writeJustifyOrAlignItemsToCss(value) {
  switch (value) {
    case 'START':
      return 'start';
    case 'CENTER':
      return 'center';
    case 'END':
      return 'end';
    case 'STRETCH':
      return 'stretch';
    default:
      return 'stretch';
  }
}
export function writeJustifyOrAlignContentToCss(value) {
  switch (value) {
    case 'START':
      return 'flex-start';
    case 'CENTER':
      return 'center';
    case 'END':
      return 'flex-end';
    case 'SPACE_BETWEEN':
      return 'space-between';
    case 'SPACE_AROUND':
      return 'space-around';
    case 'SPACE_EVENLY':
      return 'space-evenly';
    default:
      return 'stretch';
  }
}
export function isStyleOverridden(style) {
  return style !== undefined && style !== null;
}

// Regex to match a size strings like "100px", "100%", "100fr", "1.25", "-12.25px", etc.
// Ideally, we should just use CSSStyleValue.parse() but it currently lacks
// browser support.
const REGEX_FOR_SIZE_STRING = /(-?\d*\.?\d+)([a-zA-Z%]*)/;
export function stringSizeToChirpSize(sizeString) {
  const matches = sizeString.match(REGEX_FOR_SIZE_STRING);
  if (!matches) {
    throw new Error(`Invalid size string: ${sizeString}`);
  }
  const valueString = matches[1];
  const unitsString = matches[2];
  const value = parseFloat(valueString);
  const units = unitsString;
  return {
    value,
    units: CSS_TO_CHIRP_UNIT_LOOKUP[units]
  };
}
const mergeComputedTracks = (template, RowOrColumnDefinitions, trackSize) => {
  if (!template) {
    return RowOrColumnDefinitions;
  }
  const filteredTracks = template // comes out as string like "XYpx XYpx XYpx XYpx" | "[name] XYpx [name] XYpx"
  .split(' ').filter(s => s && !s.match(/^\[.*\]$/));
  const newTracks = filteredTracks.map((trackComputedSize, index) => {
    const definition = RowOrColumnDefinitions[index];
    if (definition) {
      return definition;
    }
    // implicit track converting to explicit track
    return {
      size: trackSize
    };
  });
  return newTracks;
};
export const getComputedGridDimensionsFromStyles = (node, breakpoint, computedStyles) => {
  const layout = getContainerBreakpointLayout(node, breakpoint);
  if (isStackContainerLayout(layout)) {
    return null;
  }
  return {
    rows: mergeComputedTracks(computedStyles['grid-template-rows'], layout.rows, DEFAULT_ROW_SIZE_DEFINITION),
    columns: mergeComputedTracks(computedStyles['grid-template-columns'], layout.columns, DEFAULT_COLUMN_SIZE_DEFINITION)
  };
};